import BrainboxPort from '../lib/brainbox_port.js'
import { safePushEvent } from '../lib/safe_push.js'
import { hasFeature, setBridgeFeatures, setCredentials, socketNeedsReconnect } from '../lib/auth_state.js'
import { screenshotFiles } from '../lib/onboarding_screenshots.js'
import { chatIndexMessage, newChatContextReply, openChatEvent } from '../lib/chat_origin.js'
import { vfsReply } from '../lib/vfs.js'
import { SelectedTabDraft, pruneFocusDrafts } from '../lib/selected_tab_draft.js'

// What the extension on the other end of the port can do, from the
// `bridge_features` of its `connected` message. False for every client that
// predates the list.
export { hasFeature }

/**
 * ExtensionBridge hook for Chrome extension communication.
 */

const AUTH_ERRORS = {
  EXTENSION_NOT_AVAILABLE: {
    error: 'Extension Not Available',
    details: 'Brainbox must be opened from inside Wavebox'
  },
  CONNECTION_TIMEOUT: {
    error: 'Connection Timeout',
    details: 'Extension did not respond in time. Please ensure Wavebox is running.'
  },
  CONNECTION_FAILED: {
    error: 'Connection Failed',
    details: 'Failed to establish connection with Wavebox extension'
  },
  NOT_AUTHENTICATED: {
    error: 'Not Authenticated',
    details: 'User is not logged into Wavebox'
  },
  CONNECTION_LOST: {
    error: 'Connection Lost',
    details: 'Connection to Wavebox extension was lost'
  }
}

const ExtensionBridge = {
  mounted () {
    this.extensionId = this.el.dataset.extensionId
    this.connectionTimeoutMs = parseInt(this.el.dataset.connectionTimeout || '5000', 10)
    this.connectionTimeout = null
    this.isMounted = true // Add flag to track if still mounted

    // Reconnection settings
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.baseReconnectDelay = 1000 // 1 second
    this.maxReconnectDelay = 30000 // 30 seconds
    this.reconnectTimeout = null
    this.wasConnected = false

    // Store auth credentials for reconnection
    // Read hostId from URL parameters (optional)
    const params = new URLSearchParams(window.location.search)
    this.hostId = params.get('hostId') // May be null
    pruneFocusDrafts(params.get('focusDraftId'))
    this.selectedTabDraft = new SelectedTabDraft(params.get('focusDraftId'))
    this.selectedTabDraft.save(this.el.dataset.selectedTabDraft)

    // Register port event listeners once during mount
    // These will be cleaned up in destroyed()
    this.portConnectedHandler = this.handlePortConnected.bind(this)
    this.portDisconnectedHandler = this.handlePortDisconnected.bind(this)
    this.selectionChangedHandler = this.handleSelectionChanged.bind(this)
    this.tabUpdatedHandler = this.handleTabUpdated.bind(this)
    this.boundTabClosedHandler = this.handleBoundTabClosed.bind(this)
    this.welcomeCardHandler = this.handleWelcomeCard.bind(this)
    this.mailSignedInHandler = this.handleMailSignedIn.bind(this)
    this.openChatHandler = this.handleOpenChat.bind(this)
    BrainboxPort.on('port-connected', this.portConnectedHandler)
    BrainboxPort.on('port-disconnected', this.portDisconnectedHandler)
    BrainboxPort.on('selection_changed', this.selectionChangedHandler)
    BrainboxPort.on('tab_updated', this.tabUpdatedHandler)
    BrainboxPort.on('bound_tab_closed', this.boundTabClosedHandler)
    BrainboxPort.on('welcome_card', this.welcomeCardHandler)
    BrainboxPort.on('mail_signed_in', this.mailSignedInHandler)
    BrainboxPort.on('openChat', this.openChatHandler)

    if (BrainboxPort.isConnected && window.__brainboxAuth) {
      this.wasConnected = true
      this.handlePortConnected(window.__brainboxAuth)
    } else {
      this.connect()
    }

    this.handleEvent('request_page_content', () => this.getPageContent())
    this.handleEvent('request_selection', () => this.getSelection())
    this.handleEvent('request_recent_tabs', () => {
      // eslint-disable-next-line no-console -- Trace extension requests when diagnosing missing browser context.
      console.log('[ExtensionBridge] request_recent_tabs event received')
      this.getRecentTabs()
    })
    this.handleEvent('request_active_tab', () => this.getActiveTab())
    this.handleEvent('request_tab_list', () => this.getTabList())
    this.handleEvent('fetch_tab_content', (data) => this.fetchTabContent(data.attachment_id, data.tab_id, data))
    this.handleEvent('fetch_selection_content', (data) => this.fetchSelectionContent(data.attachment_id, data.tab_id))
    this.handleEvent('request_window_tabs', () => this.getWindowTabs())
    this.handleEvent('notify_chat_changed', (data) => this.notifyChatChanged(data.chat_id))
    this.handleEvent('save_as_smart_note', (data) => this.saveAsSmartNote(data))

    // Where a chat was started: the extension keeps its own index of chats by
    // Group and App (`chatUpserted` / `chatDeleted`, one-way), and the
    // composer pill takes the user back to the tab (`focusOrigin`). The
    // server only sends these when the extension listed the feature.
    this.handleEvent('chat_index', (data) => this.sendChatIndex(data))
    this.handleEvent('focus_origin', (data) => this.focusOrigin(data))

    // Tool calling support
    this.handleEvent('execute_tool', (data) => this.executeTool(data))

    // Brainbox scopes: the server's `vfs` requests (fs tools and the
    // composer's pickers). Only sent to an extension that lists `vfs1`.
    this.handleEvent('vfs_request', (data) => this.vfsRequest(data))
    this.handleEvent('request_tool_catalog', () => this.getToolCatalog())

    // "Choose a browser to import" hands the user over to Wavebox's own import
    // UI — profile enumeration and the macOS App Data permission flow are native
    // concerns the add-space panel already handles, so there's nothing to run
    // here. The extension raises the request; the Wavebox sidebar opens the
    // panel on its import step.
    this.handleEvent('start_browser_import', () => this.startBrowserImport())

    // Chat with Settings' landing (the opening's pills).
    // Each is a Wavebox UI action with nothing to report back; an older
    // Wavebox that does not know the request rejects it, and the chat carries
    // on without it.
    this.handleEvent('start_welcome_tour', () => this.requestLandingAction('startWelcomeTour'))
    this.handleEvent('settings_chat_dive_in', () => this.requestLandingAction('settingsChatDiveIn'))

    // The October wizard's landing: sign in to two accounts. The server
    // only sends these when the extension listed `mailSignIn1`.
    this.handleEvent('open_mail_sign_in', (data) => this.openMailSignIn(data))
    this.handleEvent('request_mail_sign_ins', () => this.getMailSignIns())

    // The Undo on the first setup Wavebox built from the user's browsers.
    this.handleEvent('undo_suggested_setup', (data) => this.undoSuggestedSetup(data))

    // The screenshots the user gave the wizard's step 4: fetched once and
    // sent together as the ordinary browser-screenshot turn.
    this.handleEvent('fetch_onboarding_screenshot', () => this.fetchOnboardingScreenshot())

    // Handle conversion request from server
    this.handleEvent('convert_old_chat', async ({ chat_id }) => {
      try {
        const converted = await BrainboxPort.convertOldChat(chat_id)

        // Send converted chat back with original chat_id for tracking
        safePushEvent(this,'old_chat_converted', {
          chat_id: chat_id,
          messages: converted.messages
        })
      } catch (error) {
        console.error('[ExtensionBridge] Failed to convert chat:', error)
        safePushEvent(this,'extension_error', {
          error: 'Failed to migrate chat',
          details: error.message
        })
      }
    })

    // Handle request for fresh context when creating a new chat
    this.handleEvent('request_new_chat_context', async (request) => {
      // Invalidate in-flight refreshes before the explicit reset.
      this.selectedTabDraft.clear()
      try {
        // Fetch fresh active tab and selection from extension
        const context = await BrainboxPort.request('getInitialContext', {
          hostId: this.hostId || undefined,
          newChat: true
        })

        // Send back context without oldChats (we don't want to re-process
        // those), naming the chat that asked. The server reuses
        // handle_initial_context for this event and reads only the tab,
        // selection and origin from it; an extension that predates `origin`
        // sends none.
        safePushEvent(this, 'new_chat_context_received', newChatContextReply(request, context))
      } catch (error) {
        console.error('[ExtensionBridge] Failed to get new chat context:', error)
        // Send empty context so the flow continues
        safePushEvent(this, 'new_chat_context_received', newChatContextReply(request, null))
      }
    })

    // Handle delete request from server
    this.handleEvent('delete_old_chat_from_storage', async ({ chat_id }) => {
      try {
        await BrainboxPort.deleteOldChat(chat_id)

        // Notify server that chat was deleted
        safePushEvent(this,'old_chat_deleted', {
          chat_id: chat_id
        })
      } catch (error) {
        console.error('[ExtensionBridge] Failed to delete chat:', error)
        safePushEvent(this,'extension_error', {
          error: 'Failed to delete chat',
          details: error.message
        })
      }
    })
  },

  updated () {
    this.selectedTabDraft.save(this.el.dataset.selectedTabDraft)
  },

  connect () {
    if (typeof chrome !== 'undefined' && chrome.runtime) {
      try {
        // Event listeners are now registered once in mounted(), not here
        BrainboxPort.connect(this.extensionId)

        this.connectionTimeout = setTimeout(() => {
          if (!this.isMounted) {return} // Check if still mounted before calling pushEvent
          safePushEvent(this,'extension_error', AUTH_ERRORS.CONNECTION_TIMEOUT)
        }, this.connectionTimeoutMs)
      } catch (error) {
        console.error('Failed to connect to extension:', error)
        safePushEvent(this,'extension_error', {
          ...AUTH_ERRORS.CONNECTION_FAILED,
          details: `${AUTH_ERRORS.CONNECTION_FAILED.details}: ${error.message}`
        })
      }
    } else {
      safePushEvent(this,'extension_error', AUTH_ERRORS.EXTENSION_NOT_AVAILABLE)
    }
  },

  handlePortConnected (authData) {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout)
      this.connectionTimeout = null
    }

    const portCameBack = this.portDropped === true
    this.portDropped = false

    // Reset reconnection attempts on successful connection
    this.reconnectAttempts = 0
    this.wasConnected = true

    // Kept even when the credentials are not (a pinned dev/test pair): what
    // the page asks the extension for depends on what this one can do.
    setBridgeFeatures(authData?.bridge_features)

    // Credentials only reach the server as socket connect params, so a pair the
    // current connection isn't already carrying needs a socket cycle to take
    // effect. That is rare — a token refresh, or a different signed-in user.
    //
    // It is NOT the normal launch: app.js awaits these same credentials before
    // it connects, and this hook cannot run until the LiveView has joined, so by
    // the time we get here the socket is already authenticated with them.
    // Cycling unconditionally (as this used to) dropped a healthy connection on
    // every launch, which surfaced to the user as the "Attempting to reconnect"
    // banner and cost a duplicate mount plus a duplicate Wavebox API validation.
    if (window.liveSocket && socketNeedsReconnect(authData)) {
      // eslint-disable-next-line no-console -- Explain credential-triggered reconnects without logging credentials.
      console.log('[ExtensionBridge] New credentials, reconnecting liveSocket')
      // Update first: the params closure reads these when building the new
      // connect URL, so without this the reconnect would resend the old pair.
      setCredentials(authData)
      // Keep the window global in step. app.js writes it once, from a one-shot
      // listener, so it would otherwise still hold the superseded pair — and
      // mounted() replays it into this same handler, which would cycle the
      // socket straight back onto the old credentials.
      window.__brainboxAuth = authData
      window.liveSocket.disconnect()
      setTimeout(() => {
        window.liveSocket.connect()
      }, 100)
    }

    this.getInitialContext()

    // A sign-in during the gap sent no `mail_signed_in`, so the server asks
    // again if its sign-in steps are still open
    if (portCameBack) {
      safePushEvent(this, 'extension_port_reconnected', {})
    }
  },

  handlePortDisconnected (wasConnected) {
    if (wasConnected && this.wasConnected) {
      this.portDropped = true
      // eslint-disable-next-line no-console -- Trace connection recovery without treating a retry as an error.
      console.log('[ExtensionBridge] Connection lost, attempting to reconnect...')
      this.attemptReconnect()
    }
  },

  attemptReconnect () {
    if (!this.isMounted) {
      // eslint-disable-next-line no-console -- Explain why a scheduled reconnect stops after unmount.
      console.log('[ExtensionBridge] Hook unmounted, skipping reconnection')
      return
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[ExtensionBridge] Max reconnection attempts reached')
      safePushEvent(this,'extension_error', {
        ...AUTH_ERRORS.CONNECTION_LOST,
        details: 'Connection lost and failed to reconnect after multiple attempts. Please refresh the page.'
      })
      return
    }

    // Calculate exponential backoff delay with jitter
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    )
    // Add random jitter (±20%) to prevent thundering herd
    const jitter = exponentialDelay * 0.2 * (Math.random() - 0.5)
    const delay = Math.floor(exponentialDelay + jitter)

    this.reconnectAttempts++

    // eslint-disable-next-line no-console -- Record the retry count and delay for connection diagnostics.
    console.log(
      `[ExtensionBridge] Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    )

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    // Schedule reconnection
    this.reconnectTimeout = setTimeout(() => {
      if (!this.isMounted) {return}

      // eslint-disable-next-line no-console -- Trace connection recovery without treating a retry as an error.
      console.log('[ExtensionBridge] Attempting to reconnect...')
      this.connect()
    }, delay)
  },

  reconnected () {
    this.getInitialContext()
  },

  async getPageContent () {
    try {
      const content = await BrainboxPort.request('getPageContent')
      safePushEvent(this,'page_content_received', content)
    } catch (error) {
      console.error('Failed to get page content:', error)
      safePushEvent(this,'page_content_received', {
        type: 'error',
        message: error.message
      })
    }
  },

  async getSelection () {
    try {
      const selection = await BrainboxPort.request('getSelection')
      // eslint-disable-next-line no-console -- Preserve selection payload diagnostics for the extension bridge.
      console.log('[ExtensionBridge] getSelection received:', selection)
      safePushEvent(this,'selection_received', selection)
    } catch {
      // No selection is a normal state, not an error
      safePushEvent(this,'selection_received', null)
    }
  },

  async getActiveTab () {
    try {
      const tab = await BrainboxPort.request('getActiveTab')
      safePushEvent(this,'active_tab_received', tab || null)
    } catch (error) {
      console.error('Failed to get active tab:', error)
      safePushEvent(this,'active_tab_received', null)
    }
  },

  async getInitialContext () {
    if (!this.liveSocket.isConnected()) {
      return
    }
    const generation = this.selectedTabDraft.generation
    try {
      // Include hostId in request for pre-fetched context (if available)
      const context = await BrainboxPort.request('getInitialContext', {
        hostId: this.hostId || undefined
      })

      // Push the full initial context (may include skillExecution)
      // LiveView will detect skillExecution and trigger skill execution after processing context
      // Read the latest projection after the request: a Send or removal made
      // while it was pending must win over the earlier draft.
      const reply = this.selectedTabDraft.contextReply(context, generation, window.location)
      if (reply) { safePushEvent(this,'initial_context_received', reply) }
    } catch (error) {
      console.error('[ExtensionBridge] Failed to get initial context:', error)
      // old_chats starts empty on mount. A failed request is not initial
      // context: leave recovery available for a later successful reconnect.
    }
  },

  async getRecentTabs () {
    if (!this.liveSocket.isConnected()) {
      return
    }
    try {
      const tabs = await BrainboxPort.request('getRecentTabs', { limit: 10 })
      safePushEvent(this,'recent_tabs_received', { tabs: tabs || [] })
    } catch (error) {
      console.error('Failed to get recent tabs:', error)
      safePushEvent(this,'recent_tabs_received', { tabs: [] })
    }
  },

  async getTabList () {
    try {
      const data = await BrainboxPort.request('getTabList')
      safePushEvent(this,'tab_list_received', { tabs: data.tabs || [] })
    } catch (error) {
      console.error('Failed to get tab list:', error)
      safePushEvent(this,'tab_list_received', { tabs: [] })
    }
  },

  async getWindowTabs () {
    try {
      const data = await BrainboxPort.request('getWindowTabs')
      safePushEvent(this,'window_tabs_received', { tabs: data.tabs || [] })
    } catch (error) {
      console.error('Failed to get window tabs:', error)
      safePushEvent(this,'window_tabs_received', { tabs: [] })
    }
  },

  async fetchTabContent (attachmentId, tabId, reference = {}) {
    try {
      const data = await BrainboxPort.request('getTabContent', {
        ...(tabId != null ? { tabId } : {}),
        ...(Object.hasOwn(reference, 'controller') ? { controller: reference.controller } : {}),
        ...(Object.hasOwn(reference, 'controller_id') ? { controllerId: reference.controller_id } : {})
      })
      safePushEvent(this,'tab_content_received', {
        attachment_id: attachmentId,
        content: data.content || '',
        content_type: data.contentType || 'text'
      })
    } catch (error) {
      console.error('Failed to fetch tab content:', error)
      safePushEvent(this,'tab_content_error', {
        attachment_id: attachmentId,
        error: error.message || 'Failed to fetch content'
      })
    }
  },

  async fetchSelectionContent (attachmentId, tabId) {
    try {
      const data = await BrainboxPort.request('getSelectionContent', { tabId })
      safePushEvent(this,'selection_content_received', {
        attachment_id: attachmentId,
        content: data.content || data.text || ''
      })
    } catch (error) {
      console.error('Failed to fetch selection content:', error)
      safePushEvent(this,'selection_content_error', {
        attachment_id: attachmentId,
        error: error.message || 'Failed to fetch content'
      })
    }
  },

  async getToolCatalog () {
    try {
      const catalog = await BrainboxPort.request('getToolCatalog')
      safePushEvent(this,'tool_catalog_received', catalog || { tools: [], categories: {} })
    } catch (error) {
      console.error('Failed to get tool catalog:', error)
      safePushEvent(this,'tool_catalog_received', { tools: [], categories: {} })
    }
  },

  notifyChatChanged (chatId) {
    try {
      BrainboxPort.send('chatChanged', chatId)
    } catch (error) {
      console.error('Failed to notify chat changed:', error)
    }
  },

  /**
   * Passes a `chat_index` push from the server on to the extension as its
   * one-way `chatUpserted` / `chatDeleted`.
   * @param {Object} data - `{ op: 'upsert', entry }` or `{ op: 'delete', id }`.
   */
  sendChatIndex (data) {
    if (!hasFeature('chatIndex')) { return }
    const message = chatIndexMessage(data)
    if (!message) { return }
    try {
      BrainboxPort.send(message.type, message.data)
    } catch (error) {
      console.warn('[ExtensionBridge] Could not send the chat index:', error?.message || error)
    }
  },

  /**
   * Asks the extension to take the user back to where the chat was started.
   * @param {Object} target - `{ tabId?, serviceTabId?, appId?, groupId?, spaceId? }`
   */
  async focusOrigin (target) {
    if (!hasFeature('focusOrigin')) { return }
    try {
      await BrainboxPort.focusOrigin(target || {})
    } catch (error) {
      console.warn('[ExtensionBridge] focusOrigin is not available:', error?.message || error)
    }
  },

  /**
   * The extension's `openChat` push: open a chat the user picked from its
   * Group or App tooltip. A filter (`{ filter: {...} }`) is not handled yet.
   */
  handleOpenChat (data) {
    const event = openChatEvent(data)
    if (event) {
      safePushEvent(this, 'open_chat', event)
    }
  },

  async startBrowserImport () {
    try {
      await BrainboxPort.request('startBrowserImport')
    } catch (error) {
      console.error('[ExtensionBridge] Failed to start browser import:', error)
    }
  },

  /**
   * Asks the extension for a UI action that has no result to report.
   * @param {string} type - `startWelcomeTour` or `settingsChatDiveIn`
   */
  async requestLandingAction (type) {
    try {
      await BrainboxPort.request(type)
    } catch (error) {
      console.warn(`[ExtensionBridge] ${type} is not available:`, error?.message || error)
    }
  },

  /**
   * Asks the extension to remove what the first setup built, and reports the
   * result, or why there is none, back to the LiveView. `unavailable` marks an
   * older Wavebox that does not know the request, so nothing was touched.
   * @param {Object} data - the `undo_suggested_setup` event's payload
   * @param {string} data.proposal_id - the setup proposal to undo
   * @param {string} data.message_id - the message carrying the Undo line
   */
  /**
   * Asks the extension to switch to an onboarding Space and open its mail
   * sign-in. Nothing to report: the sign-in itself comes back as
   * `mail_signed_in` when Wavebox sees it.
   * @param {Object} data - the `open_mail_sign_in` event's payload
   */
  async openMailSignIn ({ slot }) {
    if (typeof slot !== 'string') { return }
    try {
      await BrainboxPort.request('openMailSignIn', { slot })
    } catch (error) {
      console.warn('[ExtensionBridge] openMailSignIn is not available:', error?.message || error)
    }
  },

  /**
   * Asks the extension which onboarding Spaces' mail is signed in, so a chat
   * that was closed when a sign-in happened catches up.
   */
  async getMailSignIns () {
    try {
      const result = await BrainboxPort.request('getMailSignIns')
      const signIns = Array.isArray(result?.signIns) ? result.signIns : []
      safePushEvent(this, 'mail_sign_ins', {
        slots: signIns.filter((s) => s?.signedIn === true && typeof s.slot === 'string').map((s) => s.slot)
      })
    } catch (error) {
      console.warn('[ExtensionBridge] getMailSignIns is not available:', error?.message || error)
    }
  },

  async undoSuggestedSetup ({ proposal_id: proposalId, message_id: messageId }) {
    let result
    try {
      result = await BrainboxPort.request('undoSuggestedSetup', { proposalId })
    } catch (error) {
      const message = error?.message || String(error)
      result = {
        ok: false,
        unavailable: /Unhandled port message/.test(message),
        error: message
      }
    }

    safePushEvent(this, 'suggested_setup_undo_result', {
      ...(result && typeof result === 'object' ? result : { ok: false, error: 'No result' }),
      message_id: messageId,
      proposal_id: proposalId
    })
  },

  /**
   * Fetches the wizard's screenshots (step 4 takes up to four) and hands them
   * to the upload input together as one browser-screenshot turn, the same way
   * the screenshot picker does: arm the server first, then put the files on
   * the shared input. Asks for them all with `getOnboardingScreenshots`
   * (`string[]` of data URLs) and falls back to the single
   * `getOnboardingScreenshot` a shipped Wavebox knows. When neither is known,
   * both fail or they hold nothing, nothing happens.
   */
  async fetchOnboardingScreenshot () {
    const files = screenshotFiles(await this.onboardingScreenshotDataUrls())
    if (files.length === 0) { return }

    const input = document.getElementById('file-input-label')?.querySelector('input[type="file"]')
    if (!input) { return }

    // `wizard` tells the server this turn is sent for the user, so the
    // opening's buttons move onto the reply instead of being answered away
    safePushEvent(this, 'arm_browser_screenshot', { source: 'wizard' })

    const transfer = new DataTransfer()
    files.forEach((file) => transfer.items.add(file))
    input.files = transfer.files
    input.dispatchEvent(new Event('change', { bubbles: true }))
  },

  /**
   * The wizard's screenshots as data URLs: every one from a Wavebox that
   * knows `getOnboardingScreenshots`, else the one an older Wavebox keeps.
   * @returns {Promise<string[]>}
   */
  async onboardingScreenshotDataUrls () {
    try {
      const shots = await BrainboxPort.request('getOnboardingScreenshots')
      if (Array.isArray(shots)) {
        return shots.filter((shot) => typeof shot === 'string')
      }
    } catch (error) {
      console.warn('[ExtensionBridge] getOnboardingScreenshots is not available:', error?.message || error)
    }

    try {
      const shot = await BrainboxPort.request('getOnboardingScreenshot')
      return shot?.ok && typeof shot.dataUrl === 'string' ? [shot.dataUrl] : []
    } catch (error) {
      console.warn('[ExtensionBridge] getOnboardingScreenshot is not available:', error?.message || error)
      return []
    }
  },

  async saveAsSmartNote (data) {
    try {
      await BrainboxPort.request('saveAsSmartNote', {
        chatId: data.chat_id,
        title: data.title,
        content: data.content
      })
    } catch (error) {
      console.error('[ExtensionBridge] Failed to save as smart note:', error)
    }
  },

  /**
   * Executes a tool call via the extension and returns the result to the LiveView.
   * @param {Object} data - Tool call data from LiveView
   * @param {string} data.message_id - The message ID
   * @param {string} data.tool_use_id - The tool use ID
   * @param {string} data.tool_name - The tool name to execute
   * @param {Object} data.tool_input - The tool input parameters
   */
  async executeTool (data) {
    const { message_id, tool_use_id, tool_name, tool_input } = data

    try {
      const result = await BrainboxPort.request('toolCall', {
        toolName: tool_name,
        params: tool_input || {}
      })

      // Send result back to LiveView
      safePushEvent(this,'tool_result_received', {
        message_id: message_id,
        tool_use_id: tool_use_id,
        result: result,
        is_error: !result.success
      })
    } catch (error) {
      console.error('Tool execution failed:', error)

      // Send error result back to LiveView
      safePushEvent(this,'tool_result_received', {
        message_id: message_id,
        tool_use_id: tool_use_id,
        result: {
          success: false,
          error: error.message || 'Tool execution failed'
        },
        is_error: true
      })
    }
  },

  /**
   * Runs one `vfs` request for the server and answers `vfs_result_received`
   * with its reply, or an error reply when the extension can't. The server
   * times a request out on its own, so no timer here.
   * @param {Object} data - `{ request_id, request }`
   */
  async vfsRequest (data) {
    const reply = await vfsReply(data?.request, {
      request: (type, payload) => BrainboxPort.request(type, payload),
      hasFeature
    })
    safePushEvent(this, 'vfs_result_received', { request_id: data?.request_id, reply })
  },

  /**
   * Handles live selection changes from the bound tab.
   * Forwards the event to LiveView.
   */
  handleSelectionChanged (data) {
    // eslint-disable-next-line no-console -- Preserve selection event diagnostics for the extension bridge.
    console.log('[ExtensionBridge] Live selection changed:', data)
    safePushEvent(this,'live_selection_changed', { data })
  },

  /**
   * Handles tab updates from the bound tab.
   * Forwards the event to LiveView.
   */
  handleTabUpdated (data) {
    // eslint-disable-next-line no-console -- Preserve tab update diagnostics for the extension bridge.
    console.log('[ExtensionBridge] Tab updated:', data)
    safePushEvent(this,'live_tab_updated', { data })
  },

  /**
   * Handles bound tab closure notification.
   * Forwards the event to LiveView.
   */
  handleBoundTabClosed (data) {
    // eslint-disable-next-line no-console -- Preserve bound-tab lifecycle diagnostics for the extension bridge.
    console.log('[ExtensionBridge] Bound tab closed:', data)
    safePushEvent(this,'bound_tab_closed', { data })
  },

  /**
   * Wavebox's "Your Wavebox is ready" card (in its main panel) was used:
   * `{ action: 'tour' | 'dive_in' | 'closed' }`. Its Dive in is the chat's
   * Dive in too, so the server posts the line and moves the pills
   */
  handleWelcomeCard (data) {
    const action = data?.action
    if (typeof action !== 'string') { return }
    safePushEvent(this, 'welcome_card', { action })
  },

  handleMailSignedIn (data) {
    const slot = data?.slot
    if (typeof slot !== 'string') { return }
    safePushEvent(this, 'mail_signed_in', { slot })
  },

  destroyed () {
    this.isMounted = false // Set flag to indicate hook is destroyed

    // Clear all timeouts
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout)
      this.connectionTimeout = null
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    // Remove the specific event listeners we registered in mounted()
    if (this.portConnectedHandler) {
      BrainboxPort.off('port-connected', this.portConnectedHandler)
    }
    if (this.portDisconnectedHandler) {
      BrainboxPort.off('port-disconnected', this.portDisconnectedHandler)
    }
    if (this.selectionChangedHandler) {
      BrainboxPort.off('selection_changed', this.selectionChangedHandler)
    }
    if (this.tabUpdatedHandler) {
      BrainboxPort.off('tab_updated', this.tabUpdatedHandler)
    }
    if (this.boundTabClosedHandler) {
      BrainboxPort.off('bound_tab_closed', this.boundTabClosedHandler)
    }
    if (this.welcomeCardHandler) {
      BrainboxPort.off('welcome_card', this.welcomeCardHandler)
      BrainboxPort.off('mail_signed_in', this.mailSignedInHandler)
    }
    if (this.openChatHandler) {
      BrainboxPort.off('openChat', this.openChatHandler)
    }

    BrainboxPort.disconnect()
  }
}

export default ExtensionBridge
